import { supabase } from './supabase';

/**
 * Duplicate-page check at signup (O19; frontend migration
 * 20261012210000_duplicate_page_prevention).
 *
 * /start doesn't ask for social handles (Nicky, 12 Oct: socials are set up in
 * the dashboard). signup_duplicate_check() looks for unclaimed pages with
 * exactly the display name or username the creator typed (case and spaces
 * aside; never a partial match; the creator's own page never counts). It
 * never blocks: whatever the creator answers goes on the F11 review list
 * through record_duplicate_page(), and staff follow up.
 */

export type SignupDuplicatePage = {
  id: string;
  username: string | null;
  name: string;
  avatar_url: string | null;
  bio: string | null;
};

export type SignupDuplicateCheck = {
  unclaimed: SignupDuplicatePage[];
};

/** null when the check couldn't run: signup goes on as before. */
export async function checkSignupDuplicates(fullName: string, username: string): Promise<SignupDuplicateCheck | null> {
  try {
    const { data, error } = await supabase.rpc('signup_duplicate_check', {
      p_full_name: fullName,
      p_username: username,
    });
    if (error || !data || typeof data !== 'object') return null;
    const check = data as SignupDuplicateCheck;
    return { unclaimed: Array.isArray(check.unclaimed) ? check.unclaimed : [] };
  } catch {
    return null;
  }
}

/**
 * not_mine: none of the pages is theirs; they're setting up a new page.
 * claim_requested: requestedTalentId is theirs and they have no claim link;
 * staff check it's them and send it. The database works out the matches from
 * the name and username again; it never takes them from here.
 */
export async function recordSignupDuplicate(
  talentId: string,
  choice: 'not_mine' | 'claim_requested',
  fullName: string,
  username: string,
  requestedTalentId?: string
): Promise<void> {
  const { error } = await supabase.rpc('record_duplicate_page', {
    p_talent_id: talentId,
    p_choice: choice,
    p_full_name: fullName,
    p_username: username,
    p_requested_talent_id: requestedTalentId ?? null,
  });
  if (error) throw error;
}
